import { useCarrier } from '../contexts/CarrierContext';
import { Link } from 'react-router';
import { useAuth } from '../contexts/AuthContext';

// Admin-only workspace navigator. Renders a small chip-group of every
// workspace except the one the admin is currently on, so wherever they are,
// the other places are always one click away. Non-admins see nothing.
//
// Pages pass `current` ('admin' | 'operator' | 'cable-test'); the chip for
// that workspace is hidden so only the destinations are surfaced.

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@500;600&display=swap');

  .ws-group {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    vertical-align: middle;
  }

  .ws-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: 'Vazirmatn', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: #ffffff;
    background: #2563eb;
    border: 1.5px solid rgba(255,255,255,0.25);
    border-radius: 20px;
    padding: 4px 12px 4px 10px;
    text-decoration: none;
    direction: rtl;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
    box-shadow: 0 1px 3px rgba(37,99,235,0.3);
    position: relative;
    overflow: hidden;
  }

  .ws-chip::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 60%);
    border-radius: inherit;
    pointer-events: none;
  }

  .ws-chip:hover {
    background: #1d4ed8;
    border-color: rgba(255,255,255,0.4);
    box-shadow: 0 2px 8px rgba(29,78,216,0.5);
    text-decoration: none;
    color: #ffffff;
  }

  .ws-chip:hover .ws-arrow {
    transform: translateX(-3px);
    opacity: 1;
  }

  .ws-chip-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    opacity: 0.85;
    flex-shrink: 0;
  }

  .ws-arrow {
    display: inline-flex;
    align-items: center;
    opacity: 0.7;
    transition: transform 0.15s ease, opacity 0.15s ease;
    flex-shrink: 0;
  }
`;

function ensureStyles() {
  if (typeof document !== 'undefined' && !document.getElementById('ws-chip-styles')) {
    const el = document.createElement('style');
    el.id = 'ws-chip-styles';
    el.textContent = styles;
    document.head.appendChild(el);
  }
}

function ShieldIcon() {
  return (
    <>
      <path
        d="M7 1.5 2 3.5v3.25C2 9.53 4.18 12.06 7 12.5c2.82-.44 5-2.97 5-5.75V3.5L7 1.5Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M4.5 7l1.75 1.75L9.5 5.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  );
}
function PersonIcon() {
  return (
    <path
      d="M7 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm-4 5.5c0-2.21 1.79-4 4-4s4 1.79 4 4"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  );
}
function CableIcon() {
  return (
    <>
      <path
        d="M2 7.5h3M9 7.5h3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path
        d="M5 5.5h4v4H5z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M6 3.5v2M8 3.5v2M6 9.5v2M8 9.5v2"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </>
  );
}

function ReviewIcon() {
  return <>
    <path d="M5 2H3v10h8V2H9M5 1h4v3H5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="m4.5 7.5 1.5 1.5 3.5-3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </>;
}

const WORKSPACES = [
  { id: 'admin', to: '/admin', label: 'پنل ادمین', Icon: ShieldIcon },
  { id: 'operator', to: '/operator', label: 'فضای کارشناس', Icon: PersonIcon },
  { id: 'cable-test', to: '/cable-test', label: 'تست مسیر کابل', Icon: CableIcon },
  { id: 'supervisor', to: '/supervisor', label: 'صف بازبینی', Icon: ReviewIcon },
];

export default function WorkspaceSwitcher({ current }) {
  const { profile } = useAuth();
  const { carrier } = useCarrier();
  if (!['admin', 'super_admin'].includes(profile?.role)) return null;
  ensureStyles();

  const links = WORKSPACES.filter((w) => w.id !== current);
  if (links.length === 0) return null;

  return (
    <span className="ws-group">
      {links.map(({
        id, to, label, Icon,
      }) => (
        <Link key={id} to={carrier ? `${to}?carrier=${encodeURIComponent(carrier.slug)}` : to} className="ws-chip" aria-label={label}>
          <svg className="ws-chip-icon" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <Icon />
          </svg>
          <span>{label}</span>
          <svg className="ws-arrow" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M6 2L3 5l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      ))}
    </span>
  );
}
